import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtErr, fmtMoney } from "../api";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { PhotoPicker } from "./PhotoPicker";

const KINDS = ["labour", "materials", "subcontract", "other"];
const EMPTY_LINE = () => ({ description: "", qty: 1, unit_price: "", kind: "labour", cost: "", _k: crypto.randomUUID() });

export const JobForm = ({ initial = null, properties = [], taxCfg = null, onSaved, testid = "job-form" }) => {
  const [propertyId, setPropertyId] = useState(initial?.property_id || "");
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [lines, setLines] = useState(initial ? initial.line_items.map((l) => ({ ...l, _k: crypto.randomUUID() })) : [EMPTY_LINE()]);
  const [deposit, setDeposit] = useState(initial?.deposit_percent ?? 40);
  const [photos, setPhotos] = useState(initial?.photos || []);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const setLine = (i, patch) => setLines(lines.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const subtotal = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  const tax = taxCfg?.enabled ? subtotal * (taxCfg.rate || 0) / 100 : 0;
  const total = subtotal + tax;

  const submit = async () => {
    if (!propertyId || !title.trim()) return toast.error("Pick a property and give the job a title");
    const clean = lines.filter((l) => l.description.trim()).map(({ _k, ...l }) => ({ ...l, qty: Number(l.qty) || 1, unit_price: Number(l.unit_price) || 0, cost: Number(l.cost) || 0 }));
    if (!clean.length) return toast.error("Add at least one line item");
    setBusy(true);
    try {
      const body = { property_id: propertyId, title, description, line_items: clean, deposit_percent: Number(deposit) || 0, photos };
      const r = initial ? await api.patch(`/jobs/${initial.id}`, body) : await api.post("/jobs", body);
      toast.success(initial ? "Quote updated" : "Draft quote created");
      if (onSaved) onSaved(r.data); else nav(`/jobs/${r.data.id}`);
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid={testid}>
      {!initial && (
        <section>
          <p className="label-caps mb-2">Property</p>
          <div className="space-y-2">
            {properties.map((p) => (
              <button key={p.id} type="button" data-testid={`${testid}-prop-${p.id}`} onClick={() => setPropertyId(p.id)}
                className={`w-full rounded-md border p-4 text-left font-semibold transition-colors ${propertyId === p.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
                {p.name} <span className="block text-xs font-normal opacity-80">{p.address}</span>
              </button>
            ))}
          </div>
        </section>
      )}
      <input data-testid={`${testid}-title`} className="field-input w-full" placeholder="Job title (e.g. Rebuild back deck)" value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea data-testid={`${testid}-description`} className="field-input h-20 w-full py-3" placeholder="Scope description (shown on the quote)" value={description} onChange={(e) => setDescription(e.target.value)} />
      <section>
        <p className="label-caps mb-2">Line items</p>
        <div className="space-y-3">
          {lines.map((l, i) => (
            <div key={l._k} className="card-flat space-y-2 p-3" data-testid={`${testid}-line-${i}`}>
              <div className="flex gap-2">
                <input data-testid={`${testid}-line-desc-${i}`} className="field-input flex-1" placeholder="Description (e.g. PT lumber)" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} />
                <button type="button" data-testid={`${testid}-line-del-${i}`} onClick={() => setLines(lines.filter((_, j) => j !== i))} className="h-12 w-12 shrink-0 rounded-md border border-border text-destructive"><Trash size={18} className="mx-auto" /></button>
              </div>
              <div className="flex flex-wrap gap-2">
                {KINDS.map((k) => (
                  <button key={k} type="button" data-testid={`${testid}-line-kind-${i}-${k}`} onClick={() => setLine(i, { kind: k })}
                    className={`h-9 rounded-md border px-3 text-xs font-bold capitalize transition-colors ${l.kind === k ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{k}</button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <label className="text-xs font-semibold">Qty
                  <input data-testid={`${testid}-line-qty-${i}`} type="number" className="field-input mt-1 w-full" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></label>
                <label className="text-xs font-semibold">Price $ (to owner)
                  <input data-testid={`${testid}-line-price-${i}`} type="number" className="field-input mt-1 w-full" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} /></label>
                <label className="text-xs font-semibold">Cost $ (you pay)
                  <input data-testid={`${testid}-line-cost-${i}`} type="number" className="field-input mt-1 w-full" value={l.cost} onChange={(e) => setLine(i, { cost: e.target.value })} /></label>
              </div>
            </div>
          ))}
        </div>
        <button type="button" data-testid={`${testid}-add-line`} onClick={() => setLines([...lines, EMPTY_LINE()])}
          className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm font-bold text-muted-foreground">
          <Plus size={16} weight="bold" /> Add line item
        </button>
      </section>
      <label className="block text-sm font-semibold">Deposit % (0 = no deposit)
        <input data-testid={`${testid}-deposit`} type="number" min="0" max="100" className="field-input mt-1 w-28" value={deposit} onChange={(e) => setDeposit(e.target.value)} />
      </label>
      <PhotoPicker photos={photos} setPhotos={setPhotos} min={0} testid={`${testid}-photo`} />
      <div className="card-flat space-y-1 p-4 text-sm" data-testid={`${testid}-totals`}>
        <p className="flex justify-between"><span>Subtotal</span><span className="font-bold">{fmtMoney(subtotal)}</span></p>
        {taxCfg?.enabled && <p className="flex justify-between"><span>{taxCfg.label || "HST"} ({taxCfg.rate}%)</span><span className="font-bold">{fmtMoney(tax)}</span></p>}
        <p className="flex justify-between text-base"><span>Total</span><span className="font-heading font-bold text-primary">{fmtMoney(total)}</span></p>
        {Number(deposit) > 0 && <p className="flex justify-between text-muted-foreground"><span>Deposit on approval ({deposit}%)</span><span>{fmtMoney(total * Number(deposit) / 100)}</span></p>}
      </div>
      <button type="button" data-testid={`${testid}-submit`} onClick={submit} disabled={busy} className="btn-primary-big !h-14 !text-base disabled:opacity-40">
        {busy ? "Saving…" : initial ? "Save changes" : "Create draft quote"}
      </button>
    </div>
  );
};
